import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth, grantMaintenanceExemption } from '../context/Context';
import * as Authservices from '../Services/services';

const PASSWORD_RULE = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*_])[A-Za-z\d!@#$%^&*_]{8,}$/;
const PASSWORD_HINT = 'At least 8 characters, with one uppercase letter, one digit and one special character.';

/**
 * Change Password screen — serves both flows:
 *
 *   Forced  — the admin logged in with a temporary password (fresh account or a
 *             Super-Admin reset). must_change_password is true, the backend 403s
 *             every other route, and no nav is rendered until it clears.
 *   Voluntary — any admin, any time, from the profile menu.
 */
const ChangePassword = () => {
    const navigate = useNavigate();
    const { user, logout, mustChangePassword, refreshProfile } = useAuth();

    const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [visible, setVisible] = useState({ old: false, next: false, confirm: false });

    const oldRef = useRef(null);
    const newRef = useRef(null);
    const confirmRef = useRef(null);

    const isForced = !!mustChangePassword;

    const onChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    };

    const validate = () => {
        const next = {};
        if (!form.oldPassword) {
            next.oldPassword = isForced
                ? 'Enter the temporary password you just used to sign in'
                : 'Please enter your current password';
        } else if (!form.newPassword) {
            next.newPassword = 'Please enter a new password';
        } else if (!PASSWORD_RULE.test(form.newPassword)) {
            next.newPassword = PASSWORD_HINT;
        } else if (form.newPassword === form.oldPassword) {
            next.newPassword = 'The new password must be different from the current one';
        } else if (!form.confirmPassword) {
            next.confirmPassword = 'Please confirm your new password';
        } else if (form.newPassword !== form.confirmPassword) {
            next.confirmPassword = 'The new password and confirmation do not match';
        }

        setErrors(next);
        if (next.oldPassword) oldRef.current?.focus();
        else if (next.newPassword) newRef.current?.focus();
        else if (next.confirmPassword) confirmRef.current?.focus();
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting || !validate()) return;

        setSubmitting(true);
        try {
            const res = await Authservices.updatePassword({
                old_password: form.oldPassword,
                new_password: form.newPassword,
                confirm_password: form.confirmPassword,
            });

            toast.success(res?.data?.responseMessage || 'Password updated successfully');
            setForm({ oldPassword: '', newPassword: '', confirmPassword: '' });

            // Clearing a *forced* change earns this session a pass on Admin Panel
            // Maintenance. Granted before refreshProfile() so the flag is already
            // in place when the profile update lifts the gate and re-runs the
            // maintenance check in SiteDataLoader.
            if (isForced) {
                grantMaintenanceExemption();
            }

            // must_change_password clears server-side on success — re-read the
            // profile so the gate lifts and the nav renders for the right role.
            try {
                await refreshProfile();
            } catch (err) {
                console.warn('Could not refresh profile after password change:', err?.message);
            }
            navigate('/', { replace: true });
        } catch (err) {
            const body = err?.response?.data;
            toast.error(body?.responseMessage || body?.message || 'Failed to update password. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const field = (key, label, refEl, visibilityKey, placeholder) => (
        <div className="form-group" style={{ marginBottom: 18 }}>
            <label className="form-label" htmlFor={`cp-${key}`}>{label}</label>
            <div style={{ position: 'relative' }}>
                <input
                    id={`cp-${key}`}
                    ref={refEl}
                    type={visible[visibilityKey] ? 'text' : 'password'}
                    name={key}
                    className="form-control"
                    autoComplete={key === 'oldPassword' ? 'current-password' : 'new-password'}
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={onChange}
                    style={{ paddingRight: 42 }}
                />
                <button
                    type="button"
                    onClick={() => setVisible((p) => ({ ...p, [visibilityKey]: !p[visibilityKey] }))}
                    aria-label={visible[visibilityKey] ? `Hide ${label}` : `Show ${label}`}
                    style={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', color: '#8a8a9a', padding: 4,
                    }}
                >
                    <i className={visible[visibilityKey] ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                </button>
            </div>
            {errors[key] && <span className="text-danger" style={{ fontSize: 13 }}>{errors[key]}</span>}
        </div>
    );

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 24,
        }}>
            <div className="section" style={{ width: '100%', maxWidth: 480, padding: 32, borderRadius: 16 }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: '50%', margin: '0 auto 14px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isForced ? 'rgba(245,158,11,0.15)' : 'rgba(248,37,129,0.12)',
                        color: isForced ? '#f59e0b' : '#f82581', fontSize: 22,
                    }}>
                        <i className={isForced ? 'fas fa-shield-alt' : 'fas fa-key'}></i>
                    </div>
                    <h4 style={{ margin: 0 }}>{isForced ? 'Set a new password' : 'Change Password'}</h4>
                    <p style={{ color: '#8a8a9a', fontSize: 14, margin: '8px 0 0' }}>
                        {isForced
                            ? 'You signed in with a temporary password. Set your own password to continue — no other section is available until you do.'
                            : 'Update the password for your admin account.'}
                    </p>
                    {user?.email && (
                        <p style={{ color: '#8a8a9a', fontSize: 13, margin: '6px 0 0' }}>
                            Signed in as <strong>{user.email}</strong>
                        </p>
                    )}
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    {field('oldPassword', isForced ? 'Temporary Password' : 'Current Password', oldRef, 'old',
                        isForced ? 'Enter the temporary password' : 'Enter your current password')}
                    {field('newPassword', 'New Password', newRef, 'next', 'Enter a new password')}
                    {field('confirmPassword', 'Confirm New Password', confirmRef, 'confirm', 'Re-enter the new password')}

                    <p style={{ color: '#8a8a9a', fontSize: 12, marginBottom: 20 }}>
                        <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
                        {PASSWORD_HINT}
                    </p>

                    <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                        {submitting
                            ? <><i className="fas fa-spinner fa-spin"></i> Updating…</>
                            : 'Update Password'}
                    </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: 18 }}>
                    {isForced ? (
                        <button
                            type="button"
                            onClick={logout}
                            style={{ background: 'none', border: 'none', color: '#8a8a9a', fontSize: 13, cursor: 'pointer' }}
                        >
                            Sign out instead
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            style={{ background: 'none', border: 'none', color: '#8a8a9a', fontSize: 13, cursor: 'pointer' }}
                        >
                            <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i> Back
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
